import { translate } from '@/filters';
import { Product } from '@/model/product';
import { ShoppingCart, ShoppingCartPrice } from '@/model/shopping-cart';
import { MessageBus } from '@/service/message-bus';
import { ToastMessage } from '@/service/toast-message';
import { Api } from '@/util/api';

export type ShoppingCartChangedSubscription = (newShoppingCart: ShoppingCart) => void;

export class ShoppingCartCollection {
  private static instance: ShoppingCartCollection;
  private currentShoppingCart: ShoppingCart | null = null;
  // private shoppingCartChangedSubject: ReplaySubject<ShoppingCart> = new ReplaySubject<ShoppingCart>(1);
  private shoppingCartChangedObservers: ShoppingCartChangedSubscription[] = [];

  private constructor(private api: Api, private messageBus: MessageBus, private toastMessage: ToastMessage) {}

  static getInstance(): ShoppingCartCollection {
    if (ShoppingCartCollection.instance == null) {
      ShoppingCartCollection.instance = new ShoppingCartCollection(Api.getInstance(), MessageBus.getInstance(), ToastMessage.getInstance());
    }
    return ShoppingCartCollection.instance;
  }

  getCart(): Promise<ShoppingCart> {
    if (this.currentShoppingCart != null) {
      return Promise.resolve(this.currentShoppingCart);
    }

    return this.api.getShoppingCart().then(shoppingCartData => {
      const shoppingCart = this.createShoppingCart(shoppingCartData);
      this.updateCurrentShoppingCart(shoppingCart);
      return shoppingCart;
    });
  }

  // shoppingCartChanged$(): Observable<ShoppingCart> {
  // 	return this.shoppingCartChangedSubject.asObservable()
  // 		.pipe(publishReplay(), refCount());
  // }

  shoppingCartChanged(callback: ShoppingCartChangedSubscription): () => void {
    const index = this.shoppingCartChangedObservers.push(callback);
    return () => {
      this.shoppingCartChangedObservers.splice(index, 1);
    };
  }

  putProductInShoppingCart(product: Product, quantity: number) {
    return this.api.putProductInShoppingCart(product.getKey(), quantity).then(shoppingCartData => {
      this.updateCurrentShoppingCart(this.createShoppingCart(shoppingCartData));
    });
  }

  private generateShoppingCartPriceCallPromise(): () => Promise<ShoppingCartPrice> {
    const collection = this;

    return (() => {
      let cachedPromise: Promise<any> | null = null;
      return function(this: ShoppingCart) {
        const shoppingCart = this;

        const currentShoppingCartPrice = shoppingCart.getShoppingCartPrice();
        if (currentShoppingCartPrice != null) {
          return Promise.resolve(currentShoppingCartPrice);
        }

        if (cachedPromise == null) {
          cachedPromise = collection.getShoppingCartPrice();
        }

        return cachedPromise.then(response => {
          shoppingCart.setShoppingCartPrice(response);

          const shoppingCartPrice = shoppingCart.getShoppingCartPrice();
          if (shoppingCartPrice == null) {
            return Promise.reject('unable to retrieve shopping cart price');
          }

          return Promise.resolve(shoppingCartPrice);
        });
      };
    })();
  }

  private getShoppingCartPrice(): Promise<ShoppingCartPrice> {
    return this.api.getActiveShoppingCartPrice();
  }

  private createShoppingCart(shoppingCartData: any): ShoppingCart {
    return new ShoppingCart(shoppingCartData, this.generateShoppingCartPriceCallPromise());
  }

  private updateCurrentShoppingCart(shoppingCart: ShoppingCart) {
    // todo remove this line someday when we don't have to use a message bus anymore.
    this.messageBus.publish('shoppingCart.updated', [shoppingCart]);
    this.currentShoppingCart = shoppingCart;
    this.shoppingCartChangedObservers.forEach(subscription => subscription(shoppingCart));
    // this.shoppingCartChangedSubject.next(shoppingCart);
  }
}
